import React, { useState } from 'react'

const Help = () => {
  const [expandedSections, setExpandedSections] = useState({})
  const [searchTerm, setSearchTerm] = useState('')

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }))
  }

  const isExpanded = (sectionId) => expandedSections[sectionId]

  const helpSections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: 'fas fa-rocket',
      content: [
        {
          title: 'Welcome to Your Vending Management Portal',
          content: 'This portal allows you to monitor and manage your vending devices, track inventory, view sales reports, and receive important notifications about your equipment.'
        },
        {
          title: 'Navigation',
          content: 'Use the sidebar menu to access different sections. The dashboard provides an overview of all your devices and recent activity.'
        },
        {
          title: 'Getting Help',
          content: 'Look for tooltips and help text throughout the interface. Contact support if you need additional assistance.'
        }
      ]
    },
    {
      id: 'account-security',
      title: 'Account & Security',
      icon: 'fas fa-shield-alt',
      content: [
        {
          title: 'Secure Login',
          content: 'Your account is protected with secure authentication. Use your email and password to access the portal.'
        },
        {
          title: 'Multi-Factor Authentication',
          content: 'For enhanced security, enable SMS-based two-factor authentication in your account settings.'
        },
        {
          title: 'User Roles',
          content: 'Different users have different access levels - Device Viewers can monitor equipment, while Admins can manage users and configurations.'
        },
        {
          title: 'Account Settings',
          content: 'Update your profile information, notification preferences, and security settings from the user menu.'
        }
      ]
    },
    {
      id: 'device-monitoring',
      title: 'Device Monitoring',
      icon: 'fas fa-desktop',
      content: [
        {
          title: 'Device Status',
          content: 'Monitor the online/offline status of your vending machines in real-time. Green indicators show active devices, red indicates offline.'
        },
        {
          title: 'Device Health',
          content: 'View device diagnostics, error messages, and maintenance alerts to keep your equipment running smoothly.'
        },
        {
          title: 'Maintenance Mode',
          content: 'Authorized users can enable maintenance mode to temporarily disable a device for servicing.'
        },
        {
          title: 'Location Information',
          content: 'Each device shows its location and machine name for easy identification in your network.'
        }
      ]
    },
    {
      id: 'inventory-management',
      title: 'Inventory Management',
      icon: 'fas fa-boxes',
      content: [
        {
          title: 'Stock Level Monitoring',
          content: 'View real-time inventory levels for each product in your vending machines. Low stock items are highlighted for attention.'
        },
        {
          title: 'Product Information',
          content: 'See product names, prices, and current quantities for each slot in your machines.'
        },
        {
          title: 'Stock Alerts',
          content: 'Receive automatic notifications when inventory levels are low, helping you plan restocking visits efficiently.'
        },
        {
          title: 'Restock Planning',
          content: 'Use stock level data to plan optimal restocking schedules and quantities for each location.'
        }
      ]
    },
    {
      id: 'notifications-alerts',
      title: 'Notifications & Alerts',
      icon: 'fas fa-bell',
      content: [
        {
          title: 'Alert Types',
          content: 'Receive notifications for low stock, device offline, maintenance needs, and sales milestones.'
        },
        {
          title: 'Custom Thresholds',
          content: 'Set personalized alert thresholds - choose when you want to be notified about low stock levels (e.g., below 70%, 50%, or 20%).'
        },
        {
          title: 'Notification Preferences',
          content: 'Control which types of alerts you receive and enable/disable specific notification categories based on your needs.'
        },
        {
          title: 'Alert Management',
          content: 'View all notifications in the Notification Center, mark as read, or dismiss alerts you\'ve addressed.'
        },
        {
          title: 'Priority Levels',
          content: 'Critical alerts (like device failures) are highlighted in red, warnings in orange, and informational alerts in blue.'
        }
      ]
    },
    {
      id: 'reports-analytics',
      title: 'Reports & Analytics',
      icon: 'fas fa-chart-bar',
      content: [
        {
          title: 'Transaction History',
          content: 'View detailed records of all sales transactions, including cash and electronic payments.'
        },
        {
          title: 'Sales Reports',
          content: 'Access daily, weekly, and monthly sales summaries to track your business performance.'
        },
        {
          title: 'Data Export',
          content: 'Download transaction data in CSV format for analysis in spreadsheet applications or accounting software.'
        },
        {
          title: 'Performance Metrics',
          content: 'Track key metrics like revenue per device, popular products, and peak sales times.'
        },
        {
          title: 'Monthly Reports',
          content: 'Access automatically generated monthly summary reports from the Downloads section.'
        }
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: 'fas fa-tools',
      content: [
        {
          title: 'Device Shows Offline',
          content: 'Check network connectivity at the device location. Devices may appear offline due to internet connection issues or power outages.'
        },
        {
          title: 'Missing Transactions',
          content: 'Transaction data syncs periodically. Recent sales may take a few minutes to appear in your reports.'
        },
        {
          title: 'Alert Not Working',
          content: 'Verify your notification preferences are enabled and check if you have reached your alert threshold settings.'
        },
        {
          title: 'Cannot Access Features',
          content: 'Some features require specific user permissions. Contact your administrator if you need access to additional functions.'
        },
        {
          title: 'Browser Issues',
          content: 'For best performance, use updated versions of Chrome, Firefox, Safari, or Edge. Clear browser cache if you experience loading issues.'
        },
        {
          title: 'Getting Support',
          content: 'Contact technical support for device hardware issues, payment processing problems, or account access difficulties.'
        }
      ]
    }
  ]

  const filteredSections = helpSections.filter(section => 
    searchTerm === '' || 
    section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    section.content.some(item => 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.content.toLowerCase().includes(searchTerm.toLowerCase())
    )
  )

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1">
                <i className="fas fa-question-circle text-primary me-2"></i>
                Help Center
              </h2>
              <p className="text-muted mb-0">
                Complete guide to using your Vending Management Portal
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search help topics..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Help Content */}
          <div className="row">
            <div className="col-12">
              {filteredSections.map((section) => (
                <div key={section.id} className="card mb-3">
                  <div className="card-header">
                    <h5 
                      className="mb-0 d-flex align-items-center cursor-pointer"
                      onClick={() => toggleSection(section.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className={`${section.icon} text-primary me-3`}></i>
                      {section.title}
                      <i className={`fas fa-chevron-${isExpanded(section.id) ? 'up' : 'down'} ms-auto`}></i>
                    </h5>
                  </div>
                  {isExpanded(section.id) && (
                    <div className="card-body">
                      {section.content.map((item, index) => (
                        <div key={index} className="mb-4">
                          <h6 className="text-dark fw-bold mb-2">
                            <i className="fas fa-circle text-primary me-2" style={{ fontSize: '0.5rem' }}></i>
                            {item.title}
                          </h6>
                          <p className="text-muted mb-0 ps-3">
                            {item.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              
              {filteredSections.length === 0 && searchTerm && (
                <div className="text-center py-5">
                  <i className="fas fa-search fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">No results found</h5>
                  <p className="text-muted">Try searching with different keywords</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div className="row mt-5">
            <div className="col-12">
              <div className="card bg-light">
                <div className="card-body">
                  <h5 className="card-title">
                    <i className="fas fa-lightbulb text-warning me-2"></i>
                    Quick Tips
                  </h5>
                  <div className="row">
                    <div className="col-md-6">
                      <ul className="list-unstyled">
                        <li className="mb-2">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          Check the dashboard daily for device status overview
                        </li>
                        <li className="mb-2">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          Set up custom alert thresholds for proactive management
                        </li>
                        <li className="mb-2">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          Export monthly reports for business analysis
                        </li>
                      </ul>
                    </div>
                    <div className="col-md-6">
                      <ul className="list-unstyled">
                        <li className="mb-2">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          Use notification center to stay updated on all alerts
                        </li>
                        <li className="mb-2">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          Monitor device diagnostics regularly
                        </li>
                        <li className="mb-2">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          Enable browser notifications for important alerts
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Help